import { BaseModel, type RelationConfig } from './base';

import { CustomerPass } from './customerPass';
import { Employee } from './employee';
import { ReservationResource } from './reservation';
import { Service } from './service';
import { ServiceResourceAssignment, ServiceResourceDefaultSelection } from './serviceResource';
import { Voucher } from './voucher';
import { plugin as $date } from '~/plugins/date';

// Enums

export enum ReservationBufferTimeMode {
  reservationAggregate = 0,
  reservationLongest = 1,
  interService = 2,
  employeeAggregate = 3,
  employeeLongest = 4,
}

export const ReservationBufferTimeModeLabels: Record<ReservationBufferTimeMode, string> = {
  [ReservationBufferTimeMode.reservationAggregate]: 'reservation.bufferTimeMode.reservationAggregate',
  [ReservationBufferTimeMode.reservationLongest]: 'reservation.bufferTimeMode.reservationLongest',
  [ReservationBufferTimeMode.interService]: 'reservation.bufferTimeMode.interService',
  [ReservationBufferTimeMode.employeeAggregate]: 'reservation.bufferTimeMode.employeeAggregate',
  [ReservationBufferTimeMode.employeeLongest]: 'reservation.bufferTimeMode.employeeLongest',
};

export enum ReservationServiceDiscountType {
  fixed = 'fixed',
  percentage = 'percentage',
}

export const ReservationServiceDiscountTypeLabels: Record<ReservationServiceDiscountType, string> = {
  [ReservationServiceDiscountType.fixed]: 'general.fixed',
  [ReservationServiceDiscountType.percentage]: 'general.percentage',
};

export enum ReservationServiceDiscountTaxType {
  post = 'post',
  pre = 'pre',
}

export const ReservationServiceDiscountTaxTypeLabels: Record<ReservationServiceDiscountTaxType, string> = {
  [ReservationServiceDiscountTaxType.post]: 'general.post',
  [ReservationServiceDiscountTaxType.pre]: 'general.pre',
};

export enum ReservationServiceDiscountSource {
  association = 'association',
  manual = 'manual',
  voucher = 'voucher',
  discount = 'discount',
}

export const ReservationServiceDiscountSourceLabels: Record<ReservationServiceDiscountSource, string> = {
  [ReservationServiceDiscountSource.association]: 'general.association',
  [ReservationServiceDiscountSource.manual]: 'general.manual',
  [ReservationServiceDiscountSource.voucher]: 'general.voucher',
  [ReservationServiceDiscountSource.discount]: 'general.discount',
};

// Helper Classes

export class ReservationServiceDiscount extends BaseModel {
  type!: ReservationServiceDiscountType;
  taxType!: ReservationServiceDiscountTaxType;
  source!: ReservationServiceDiscountSource;
  amount!: number;

  get isSourceAssociation(): boolean {
    return this.source === ReservationServiceDiscountSource.association;
  }

  get isSourceManual(): boolean {
    return this.source === ReservationServiceDiscountSource.manual;
  }

  get isSourceVoucher(): boolean {
    return this.source === ReservationServiceDiscountSource.voucher;
  }

  get isSourceDiscount(): boolean {
    return this.source === ReservationServiceDiscountSource.discount;
  }

  get isTypeFixed(): boolean {
    return this.type === ReservationServiceDiscountType.fixed;
  }

  get isTypePercentage(): boolean {
    return this.type === ReservationServiceDiscountType.percentage;
  }
}

// Main Model

export class ReservationServiceModel extends BaseModel {
  idReservationService!: number;
  title!: string;
  price!: number;
  startTime!: number;
  duration!: number;
  finishing!: number;
  processing!: number;
  bufferTimePre!: number | null;
  bufferTimePost!: number | null;
  bufferTimeMode!: ReservationBufferTimeMode;
  allowInternet!: boolean;
  customerMax!: number;
  discount!: number | null;
  discountType!: ReservationServiceDiscountType;
  discounts!: ReservationServiceDiscount[];
  hasImage!: boolean;
  images!: Record<string, string> | null;
  employee!: Employee | number | null;
  service!: Service | null;
  resources!: ReservationResource[];
  voucher!: Voucher | null;
  customerPass!: CustomerPass | null;

  static override relations(): RelationConfig {
    return {
      customerPass: { type: CustomerPass },
      employee: { type: Employee },
      resources: { type: ReservationResource },
      service: { type: Service },
      voucher: { type: Voucher },
      discounts: { type: ReservationServiceDiscount },
    };
  }

  override objectID(): string | null {
    return this.idReservationService?.toString() ?? null;
  }

  setValues(presets: Record<string, any> = {}): void {
    const service = this.service || ({} as any);
    const employee = this.hasEmployee
      ? (this.employee as Employee).getEmployeeServiceForService?.(service) || {}
      : {};

    const defaults: Record<string, any> = {
      price: 0,
      startTime: 0,
      duration: 0,
      finishing: 0,
      processing: 0,
      bufferTimePre: 0,
      bufferTimePost: 0,
      allowInternet: true,
      customerMax: 1,
    };

    for (const key in defaults) {
      if (presets[key] != null) {
        defaults[key] = presets[key];
      } else if ((employee as any)?.[key] != null) {
        defaults[key] = (employee as any)[key];
      } else if ((service as any)?.[key] != null) {
        defaults[key] = (service as any)[key];
      }
    }

    Object.assign(this, defaults);
  }

  setResources(
    assignment: ServiceResourceAssignment,
    existingResources: ReservationResource[] | undefined,
    allowFirstAvailable = true
  ): void {
    const service = this.service;
    const resourcesForAssignment = service?.resources
      ? (service as any).resourcesForAssignment(assignment)
      : [];

    const items: ReservationResource[] = [];
    const usedResources: number[] = [];

    for (const serviceResource of resourcesForAssignment) {
      const existing = existingResources
        ?.filter((resource: any) => resource.serviceResource?.idServiceResource === serviceResource.idServiceResource)
        .map((resource: any) => resource.resource);

      for (let i = 0; i < serviceResource.quantity; i++) {
        const reservationResource = new ReservationResource({ serviceResource });
        (reservationResource as any).count = i;

        if (existingResources !== undefined) {
          const available = (existing || [])
            .filter((r: any) => !usedResources.includes(r?.idEmployee));

          (reservationResource as any).resource = available[0];

          if ((reservationResource as any).resource) {
            usedResources.push((reservationResource as any).resource.idEmployee);
          }
        }

        if (!(reservationResource as any).resource) {
          if (serviceResource.defaultSelection === ServiceResourceDefaultSelection.firstAvailable) {
            if (allowFirstAvailable) {
              (reservationResource as any).resource = ServiceResourceDefaultSelection.firstAvailable;
            } else {
              (reservationResource as any).resource = null;
            }
          } else {
            (reservationResource as any).resource = ServiceResourceDefaultSelection.none;
          }
        }

        items.push(reservationResource);
      }
    }

    this.resources = items;
  }

  get defaults(): Record<string, any> {
    const service = this.service || ({} as any);
    const employee = this.hasEmployee && this.isFirstAvailable
      ? (this.employee as Employee).getEmployeeServiceForService?.(service) || {}
      : {};

    const result: Record<string, any> = {
      price: 0,
      startTime: 0,
      duration: 0,
      finishing: 0,
      processing: 0,
      bufferTimePre: 0,
      bufferTimePost: 0,
      allowInternet: true,
      customerMax: 1,
    };

    for (const key in result) {
      result[key] = (employee as any)[key] || (service as any)[key] || result[key];
    }

    return result;
  }

  get image(): string | null {
    return this.hasImage ? this.images?.square || null : null;
  }

  get initials(): string {
    return this.title?.charAt(0).toUpperCase() ?? '';
  }

  get colour(): string {
    if (this.hasService) {
      return '#' + (this.service as Service).borderHexColour;
    }
    return '#bbbbbb';
  }

  get discountTotal(): number {
    const discount = this.discount;
    if (discount == null) return 0;

    if (this.isDiscountTypeFixed) return discount;
    if (this.isDiscountTypePercentage) return this.price * (discount / 100);

    return 0;
  }

  get discountedPriceLabel(): number {
    return this.price - this.discountTotal;
  }

  get hasService(): boolean {
    return !!this.service;
  }

  get hasEmployee(): boolean {
    return !!this.employee;
  }

  get hasResources(): boolean {
    return !!this.resources?.length;
  }

  get isFirstAvailable(): boolean {
    return this.employee === 0;
  }

  get hasPreBuffer(): boolean {
    return this.bufferTimePre !== null;
  }

  get hasPostBuffer(): boolean {
    return this.bufferTimePost !== null;
  }

  get hasStartTime(): boolean {
    return this.startTime !== 0;
  }

  get isServiceCustomerTypeSingle(): boolean {
    return !!(this.service as any)?.isServiceCustomerTypeSingle;
  }

  get isPriceModified(): boolean {
    return this.hasService && this.defaults != null && this.price !== this.defaults.price;
  }

  get isTimingModified(): boolean {
    return this.hasService && this.defaults != null && (
      this.isDurationModified || this.isProcessingModified ||
      this.isFinishingModified || this.isPreBufferModified || this.isPostBufferModified
    );
  }

  get isDurationModified(): boolean {
    return this.hasService && this.defaults != null && this.duration !== this.defaults.duration;
  }

  get isProcessingModified(): boolean {
    return this.hasService && this.defaults != null && this.processing !== this.defaults.processing;
  }

  get isFinishingModified(): boolean {
    return this.hasService && this.defaults != null && this.finishing !== this.defaults.finishing;
  }

  get isPreBufferModified(): boolean {
    return this.hasService && this.defaults != null && this.bufferTimePre !== this.defaults.bufferTimePre;
  }

  get isPostBufferModified(): boolean {
    return this.hasService && this.defaults != null && this.bufferTimePost !== this.defaults.bufferTimePost;
  }

  get isDiscountTypeFixed(): boolean {
    return this.discountType === ReservationServiceDiscountType.fixed;
  }

  get isDiscountTypePercentage(): boolean {
    return this.discountType === ReservationServiceDiscountType.percentage;
  }

  get startLabel(): string | Date | null {
    return $date.minutes(this.startTime);
  }

  get finishLabel(): string | Date | null {
    const startTime = Number(this.startTime);
    const duration = Number(this.duration);
    return $date.minutes(startTime + duration);
  }

  get durationLabel(): number {
    return this.duration;
  }
}
